import { useDeleteWorkTasksMutation } from "@common/api/work-tasks"
import { handleError } from "@common/utils"
import { createFileRoute, redirect, useNavigate } from "@tanstack/react-router"
import z from "zod"
import { DeleteDialog } from "@/components/layout/list-table-menu"
import useCustomToast from "@/hooks/use-custom-toast"
import { requirePermission } from "@/lib/require-permission"
import { taskCount } from "../-lib/lifecycle"

const searchSchema = z.object({
  ids: z.preprocess(
    (value) =>
      Array.isArray(value) ? value : value === undefined ? undefined : [value],
    z.array(z.string().uuid()).catch([]),
  ),
})

export const Route = createFileRoute(
  "/_layout/work-orders/$workOrderId/tasks/delete",
)({
  validateSearch: (search) => searchSchema.parse(search),
  beforeLoad: async ({ context: { queryClient }, params, search }) => {
    await requirePermission(queryClient, "work_task:delete", "/")
    if (search.ids.length === 0) {
      throw redirect({ to: "/work-orders/$workOrderId", params, search: true })
    }
  },
  component: DeleteTasksDialog,
})

function DeleteTasksDialog(): React.ReactElement {
  const { workOrderId } = Route.useParams()
  const { ids } = Route.useSearch()
  const navigate = useNavigate()
  const showToast = useCustomToast()
  const remove = useDeleteWorkTasksMutation()
  const deleted = taskCount(ids.length)

  const close = () =>
    navigate({
      from: Route.fullPath,
      to: "/work-orders/$workOrderId",
      params: { workOrderId },
      search: ({ ids: _, ...previous }) => previous,
    })

  return (
    <DeleteDialog
      noun={ids.length === 1 ? "task" : deleted}
      pending={remove.isPending}
      onCancel={close}
      onConfirm={() =>
        remove.mutate(
          { work_package_id: workOrderId, deleteWorkTasks: { ids } },
          {
            onSuccess: () => {
              showToast("Deleted", `${deleted} deleted.`, "success")
              close()
            },
            onError: (error) => handleError(error, showToast),
          },
        )
      }
    />
  )
}
